import React, { useCallback, useMemo, useState } from 'react';
import { GoogleMap, Marker, Circle, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { COUNTRIES } from '../../uitilites/countries';
import { SaaSServices } from '../../services/saasServices';
import { GOOGLE_MAPS_LOADER_OPTIONS, MAP_STYLE_PRESETS } from './GoogleMapsConfig';

const DEFAULT_COUNTRY = Object.freeze({
  iso2: '',
  iso3: '',
  name: 'Select a country',
  lat: 20,
  lng: 0,
});

const countryFor = (countryIso2, countryName) => {
  const normalizedIso2 = String(countryIso2 || '').trim().toUpperCase();
  const normalizedName = String(countryName || '').trim().toLowerCase();
  return COUNTRIES.find((country) => country.iso2 === normalizedIso2)
    || COUNTRIES.find((country) => country.name.toLowerCase() === normalizedName)
    || DEFAULT_COUNTRY;
};

const boundaryGeometry = (boundary) => (
  boundary?.type === 'Feature' ? boundary.geometry : boundary
);

const boundaryPolygons = (boundary) => {
  const geometry = boundaryGeometry(boundary);
  if (geometry?.type === 'Polygon') return [geometry.coordinates || []];
  if (geometry?.type === 'MultiPolygon') return geometry.coordinates || [];
  return [];
};

const coordinate = (pair) => {
  const longitude = Number(pair?.[0]);
  const latitude = Number(pair?.[1]);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { lat: latitude, lng: longitude }
    : null;
};

export const operationsAreaBoundaryPaths = (boundary) => boundaryPolygons(boundary)
  .map((polygon) => (polygon || [])
    .map((ring) => (ring || []).map(coordinate).filter(Boolean))
    .filter((ring) => ring.length >= 3))
  .filter((polygon) => polygon.length);

export const operationsAreaBoundarySvgPath = (boundary) => {
  const polygons = operationsAreaBoundaryPaths(boundary);
  const points = polygons.flat(2);
  if (!points.length) return '';

  const rawLongitudes = points.map((point) => point.lng);
  const crossesAntimeridian = Math.max(...rawLongitudes) - Math.min(...rawLongitudes) > 180;
  const projectedLongitude = (longitude) => (
    crossesAntimeridian && longitude < 0 ? longitude + 360 : longitude
  );
  const longitudes = points.map((point) => projectedLongitude(point.lng));
  const latitudes = points.map((point) => point.lat);
  const minLongitude = Math.min(...longitudes);
  const maxLongitude = Math.max(...longitudes);
  const minLatitude = Math.min(...latitudes);
  const maxLatitude = Math.max(...latitudes);
  const longitudeSpan = Math.max(0.0001, maxLongitude - minLongitude);
  const latitudeSpan = Math.max(0.0001, maxLatitude - minLatitude);
  const width = 100;
  const height = 58;
  const padding = 5;
  const scale = Math.min(
    (width - (padding * 2)) / longitudeSpan,
    (height - (padding * 2)) / latitudeSpan,
  );
  const projectedWidth = longitudeSpan * scale;
  const projectedHeight = latitudeSpan * scale;
  const offsetX = (width - projectedWidth) / 2;
  const offsetY = (height - projectedHeight) / 2;

  return polygons.map((polygon) => polygon.map((ring) => ring.map((point, index) => {
    const x = offsetX + ((projectedLongitude(point.lng) - minLongitude) * scale);
    const y = offsetY + ((maxLatitude - point.lat) * scale);
    return `${index ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ') + ' Z').join(' ')).join(' ');
};

// The info strip shown along the bottom of BOTH the live map and the fallback.
const InfoStrip = ({ hasCountry, countryLabel, coordinateText, safeRadiusKm }) => (
  <div
    style={{
      alignItems: 'center',
      backdropFilter: 'blur(8px)',
      background: 'rgba(5, 10, 21, 0.82)',
      borderTop: '1px solid rgba(71, 85, 105, 0.45)',
      bottom: 0,
      display: 'grid',
      gap: '10px',
      gridTemplateColumns: 'minmax(0, 1fr) auto auto',
      left: 0,
      padding: '11px 14px',
      position: 'absolute',
      right: 0,
      zIndex: 2,
    }}
  >
    <div style={{ minWidth: 0 }}>
      <span style={{ color: '#64748b', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Operations Area
      </span>
      <strong style={{ color: '#f1f5f9', display: 'block', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {hasCountry ? countryLabel : 'Country not selected'}
      </strong>
    </div>
    <div>
      <span style={{ color: '#64748b', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Centre
      </span>
      <strong style={{ color: '#cbd5e1', display: 'block', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '10px', whiteSpace: 'nowrap' }}>
        {coordinateText}
      </strong>
    </div>
    <div>
      <span style={{ color: '#64748b', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Radius
      </span>
      <strong style={{ color: '#4ade80', display: 'block', fontSize: '11px', whiteSpace: 'nowrap' }}>
        {safeRadiusKm.toLocaleString()} km
      </strong>
    </div>
  </div>
);

/**
 * Operations Area preview for the SaaS control plane.
 *
 * Renders the REAL Google map (marker + watch-radius circle) whenever the
 * shared Maps loader succeeds — same visual language as the institution-side
 * operations map. SaaS administration must remain usable when the key,
 * billing, or referrer configuration is unavailable, so any load/auth failure
 * falls back to the dependency-free static preview below.
 */
export default function SaaSOperationsAreaMap({
  countryIso2,
  countryName,
  radiusKm,
}) {
  const country = useMemo(
    () => countryFor(countryIso2, countryName),
    [countryIso2, countryName],
  );
  const safeRadiusKm = Math.min(5000, Math.max(100, Number(radiusKm) || 100));
  const radiusDiameter = 92 + ((safeRadiusKm - 100) / 4900) * 84;
  const hasCountry = Boolean(country.iso2);
  const latitude = Number(country.lat);
  const longitude = Number(country.lng);
  const coordinateText = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  const [countryBoundary, setCountryBoundary] = useState(null);
  const boundaryPaths = useMemo(
    () => operationsAreaBoundaryPaths(countryBoundary),
    [countryBoundary],
  );
  const boundarySvgPath = useMemo(
    () => operationsAreaBoundarySvgPath(countryBoundary),
    [countryBoundary],
  );

  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const [authFailed, setAuthFailed] = useState(false);
  const [mapRef, setMapRef] = useState(null);

  React.useEffect(() => {
    let current = true;
    setCountryBoundary(null);
    if (!hasCountry) return () => { current = false; };

    SaaSServices.getOperationsAreaCountryBoundary(country.iso2)
      .then((response) => {
        if (!current) return;
        const payload = response?.status === true && Object.prototype.hasOwnProperty.call(response, 'data')
          ? response.data
          : response;
        setCountryBoundary(payload?.boundary || null);
      })
      .catch(() => {
        if (current) setCountryBoundary(null);
      });
    return () => { current = false; };
  }, [country.iso2, hasCountry]);

  // Google reports an invalid/misconfigured key through this global hook —
  // chain rather than clobber so operational maps keep their own handling.
  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => { setAuthFailed(true); if (typeof prev === 'function') prev(); };
  }, []);

  // Keep both the selected country outline and the watch radius visible.
  const fitToArea = useCallback((map, lat, lng, km, polygons) => {
    if (!map || !window.google?.maps) return;
    const dLat = km / 111;
    const dLng = km / (111 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
    const bounds = new window.google.maps.LatLngBounds(
      { lat: lat - dLat, lng: lng - dLng },
      { lat: lat + dLat, lng: lng + dLng },
    );
    polygons.flat(2).forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 8);
  }, []);

  React.useEffect(() => {
    if (mapRef) fitToArea(mapRef, latitude, longitude, safeRadiusKm, boundaryPaths);
  }, [mapRef, latitude, longitude, safeRadiusKm, boundaryPaths, fitToArea]);

  if (isLoaded && !loadError && !authFailed) {
    return (
      <div
        data-testid="saas-operations-map"
        style={{
          border: '1px solid rgba(71, 85, 105, 0.55)',
          borderRadius: '12px',
          height: '230px',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={{ lat: latitude, lng: longitude }}
          zoom={4}
          onLoad={onMapLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            clickableIcons: false,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            // SaaS control plane is a dark surface — match it.
            styles: MAP_STYLE_PRESETS.dark.styles,
          }}
        >
          {hasCountry && <Marker position={{ lat: latitude, lng: longitude }} />}
          {boundaryPaths.map((paths, index) => (
            <Polygon
              key={`${country.iso2}-boundary-${index}`}
              paths={paths}
              options={{
                strokeColor: '#38bdf8',
                strokeOpacity: 1,
                strokeWeight: 3,
                fillColor: '#0ea5e9',
                fillOpacity: 0.18,
                clickable: false,
                zIndex: 1,
              }}
            />
          ))}
          {hasCountry && (
            <Circle
              center={{ lat: latitude, lng: longitude }}
              radius={safeRadiusKm * 1000}
              options={{
                strokeColor: '#16a34a',
                strokeOpacity: 0.85,
                strokeWeight: 2,
                fillColor: '#22c55e',
                fillOpacity: 0.08,
                clickable: false,
                zIndex: 0,
              }}
            />
          )}
        </GoogleMap>
        <InfoStrip
          hasCountry={hasCountry}
          countryLabel={country.name}
          coordinateText={coordinateText}
          safeRadiusKm={safeRadiusKm}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="saas-operations-map"
      role="img"
      aria-label={`${country.name} operations area, ${safeRadiusKm.toLocaleString()} kilometre watch radius. Map preview unavailable.`}
      style={{
        background: 'radial-gradient(circle at 50% 48%, rgba(34, 197, 94, 0.12), transparent 36%), linear-gradient(145deg, #111b32 0%, #07101f 68%, #050a15 100%)',
        border: '1px solid rgba(71, 85, 105, 0.55)',
        borderRadius: '12px',
        color: '#e2e8f0',
        height: '230px',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          backgroundImage: 'linear-gradient(rgba(148, 163, 184, 0.075) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.075) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          inset: 0,
          maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
          position: 'absolute',
        }}
      />

      {boundarySvgPath && (
        <svg
          aria-hidden="true"
          data-testid="saas-operations-country-boundary"
          viewBox="0 0 100 58"
          preserveAspectRatio="xMidYMid meet"
          style={{
            height: 'calc(100% - 97px)',
            left: '18px',
            overflow: 'visible',
            pointerEvents: 'none',
            position: 'absolute',
            top: '36px',
            width: 'calc(100% - 36px)',
            zIndex: 1,
          }}
        >
          <path
            d={boundarySvgPath}
            fill="rgba(14, 165, 233, 0.18)"
            fillRule="evenodd"
            stroke="#38bdf8"
            strokeLinejoin="round"
            strokeWidth="1.25"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}

      <div style={{ left: '16px', position: 'absolute', top: '15px', zIndex: 2 }}>
        <strong style={{ color: '#f8fafc', display: 'block', fontSize: '13px', lineHeight: 1.35 }}>
          Map preview unavailable
        </strong>
        <span style={{ color: '#94a3b8', display: 'block', fontSize: '11px', marginTop: '2px' }}>
          Live operations map
        </span>
      </div>

      <div
        aria-hidden="true"
        style={{
          border: '1px solid rgba(74, 222, 128, 0.52)',
          borderRadius: '50%',
          boxShadow: '0 0 0 14px rgba(34, 197, 94, 0.035), inset 0 0 28px rgba(34, 197, 94, 0.08)',
          height: `${radiusDiameter}px`,
          left: '50%',
          position: 'absolute',
          top: '49%',
          transform: 'translate(-50%, -50%)',
          width: `${radiusDiameter}px`,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          alignItems: 'center',
          background: '#16a34a',
          border: '3px solid #dcfce7',
          borderRadius: '50%',
          boxShadow: '0 0 0 8px rgba(34, 197, 94, 0.15), 0 4px 16px rgba(0, 0, 0, 0.45)',
          color: '#fff',
          display: 'flex',
          height: '30px',
          justifyContent: 'center',
          left: '50%',
          position: 'absolute',
          top: '49%',
          transform: 'translate(-50%, -50%)',
          width: '30px',
        }}
      >
        <MapPin size={14} strokeWidth={2.4} />
      </div>

      <InfoStrip
        hasCountry={hasCountry}
        countryLabel={country.name}
        coordinateText={coordinateText}
        safeRadiusKm={safeRadiusKm}
      />
    </div>
  );
}

export { countryFor as operationsAreaCountry };
